import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "clearableValues", "value", "onChange", "index", "relatedRecipients", "contactProperty", "emailBlocklistStatus", "senderDeliveryIdentifierValue", "filter"];
import { memo, useCallback, useRef, useState, forwardRef, useMemo, useImperativeHandle, useContext } from 'react';
import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import I18n from 'I18n';
import { useContactPermissions } from 'conversations-thread-data/contact-permissions/public/hooks';
import { validateEmail } from 'composer-data/email/public/operators';
import { postRecipientGenericSearch } from 'composer-data/contacts/public/clients';
import debounce from 'react-utils/debounce';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { useEmailDeliverabilityStatuses } from 'composer-data/email/public/hooks';
import { getGenericIdentifierOptions, isGroupedGenericOptions } from '../operators/getGenericIdentifierOptions';
import GenericRecipientTag from './GenericRecipientTag';
import { GenericIdentifierRecord } from 'conversations-thread-data/recipients/public/GenericIdentifierRecord';
import formatPhoneNumber from 'I18n/utils/formatPhoneNumber';
import { HS_EMAIL_ADDRESS, HS_PHONE_NUMBER } from 'conversations-message-history/common-message-format/constants/deliveryIdentifierTypes';
import { useWhatsAppSubscription } from '../../../whatsapp/protected/hooks';
import { WARecipientShepherd } from './WARecipientShepherd';
import { searchForRecipient } from '../../../usage-tracking/events/metadataEditorInteractions';
import { useEditorCapability } from 'composer-data/channel-capabilities/public/hooks';
import { EditorTrackerContext } from '../../../usage-tracking/context/EditorTrackerContext';
import { Select } from 'foundations-components/transitional/components/Select';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
const SEARCH_INPUT_DEBOUNCE_MS = 100;
function optionsContainsString(searchString, element) {
  function containsSearchText({
    text
  }) {
    return text.toLowerCase().includes(searchString && searchString.toLowerCase());
  }
  return containsSearchText(element) || isGroupedGenericOptions(element) && element.options.some(containsSearchText);
}
const _GenericRecipientSearchInput = (props, ref) => {
  const {
      autoFocus,
      clearableValues,
      value,
      onChange,
      index,
      relatedRecipients,
      contactProperty,
      emailBlocklistStatus,
      senderDeliveryIdentifierValue,
      filter
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [selectedOptions, setSelectedOptions] = useState();
  const internalValue = (selectedOptions || value).toArray().map(genericRecord => genericRecord.deliveryIdentifierValue || genericRecord.address || '');
  const {
    deliverabilityStatuses
  } = useEmailDeliverabilityStatuses({
    emailAddresses: internalValue
  });
  const {
    contactPermissions
  } = useContactPermissions({
    emailAddresses: ImmutableSet(internalValue)
  });
  const {
    isSubscribedForTemplates,
    loading: whatsAppSubscriptionLoading
  } = useWhatsAppSubscription({
    deferred: contactProperty !== HS_PHONE_NUMBER || !senderDeliveryIdentifierValue,
    endUserPhoneNumber: internalValue[0],
    hubSpotCustomerPhoneNumber: senderDeliveryIdentifierValue || ''
  });
  const getValidationData = (emailAddress, type) => {
    if (type === HS_EMAIL_ADDRESS) {
      const validationData = validateEmail(emailAddress, deliverabilityStatuses, contactPermissions);
      return {
        isValid: validationData.isEmailValid,
        reason: validationData.reason
      };
    }
    if (contactProperty === HS_PHONE_NUMBER) {
      const isInvalid = !whatsAppSubscriptionLoading && !isSubscribedForTemplates;
      return {
        isValid: !isInvalid,
        reason: isInvalid ? 'WHATSAPP_NOT_OPTED_IN' : undefined
      };
    }
    return {
      isValid: true
    };
  };
  const formatter = (optionText, type) => {
    if (type === HS_PHONE_NUMBER) {
      return formatPhoneNumber(optionText, '');
    }
    return optionText;
  };
  const searchResults = useRef({});
  const searchText = useRef('');
  const [options, setOptions] = useState();

  // generate options and value for values that may have been cached and
  // won't be in the search results
  const valueOptions = getGenericIdentifierOptions(selectedOptions || value, {
    clearable: clearableValues,
    getValidationData,
    formatter,
    emailBlocklistStatus
  });
  const handleChange = useCallback(({
    target: {
      value: changeValue
    }
  }) => {
    // map search results to id-keyed email address records
    const indexedSearchResults = {};
    Object.values(searchResults.current).forEach(genericRecords => {
      genericRecords.forEach(genericRecord => {
        if (genericRecord.deliveryIdentifierValue) {
          indexedSearchResults[genericRecord.deliveryIdentifierValue] = genericRecord;
        }
      });
    });

    // filter out addresses that may have been removed
    const nextValue = value.filter(genericRecord => !!genericRecord.address && changeValue.includes(genericRecord.address));

    // add incoming addresses
    let addedRecipients = ImmutableList();
    changeValue.forEach(address => {
      const hasValue = nextValue.find(emailAddressRecord => emailAddressRecord.address === address);
      if (!hasValue) {
        if (indexedSearchResults[address]) {
          addedRecipients = addedRecipients.push(indexedSearchResults[address]);
        } else if (relatedRecipients) {
          const relatedRecipient = relatedRecipients.find(recipient => recipient.address === address);
          if (relatedRecipient) {
            addedRecipients = addedRecipients.push(relatedRecipient);
          }
        }
      }
    });
    const recipientList = ImmutableList(nextValue.concat(addedRecipients));
    if (recipientList.size) {
      setSelectedOptions(recipientList);
    } else {
      setSelectedOptions(undefined);
    }
    onChange(recipientList);
  }, [onChange, value, relatedRecipients, setSelectedOptions]);
  const createNewEmailEntry = useCallback(() => {
    return new GenericIdentifierRecord({
      address: searchText.current,
      deliveryIdentifierType: HS_EMAIL_ADDRESS,
      deliveryIdentifierValue: searchText.current
    });
  }, []);
  const {
    trackEvent
  } = useContext(EditorTrackerContext);
  const handleLoadOptions = useMemo(() => debounce(userQuery => {
    searchText.current = userQuery;
    if (!userQuery || !userQuery.length) {
      setOptions(undefined);
      return;
    }
    if (index === 0) trackEvent(searchForRecipient({
      contactProperty
    }));

    // search for recipients
    postRecipientGenericSearch({
      searchTerm: userQuery,
      contactProperty,
      query: {
        requestAction: 'COMMUNICATE'
      }
    }).then(genericRecords => {
      searchResults.current[userQuery] = genericRecords;
      const newOptions = getGenericIdentifierOptions(ImmutableList(genericRecords), {
        clearable: clearableValues,
        emailBlocklistStatus,
        filter
      });
      // bake incoming results in with the options for existing values
      setOptions(newOptions);
    }).catch(error => {
      reportError({
        error: new Error(`Error in generic recipient search: ${error.message}`),
        extra: {
          error,
          requestArgs: {
            searchTerm: userQuery,
            deliveryIdentifier: 'generic'
          }
        }
      });
    });
  }, SEARCH_INPUT_DEBOUNCE_MS), [clearableValues, contactProperty, index, trackEvent, emailBlocklistStatus, filter]);
  const channelAllowsMultipleRecipients = useEditorCapability('allowMultipleRecipients');

  // Get options logic which creates and passes new option if current user query does not return any contacts
  // Otherwise return options (from API) with already selected options OR only selected options with a dummy
  const getOptions = () => {
    // dummyOption for bypassing default 'all options are selected` text
    const onlyOneRecipientDummyOption = {
      name: '',
      address: '',
      text: I18n.text('composer-ui.metadata-editor.generic-recipient-search-input.onlyOneRecipient'),
      value: '',
      clearableValue: false,
      disabled: true
    };
    if (!channelAllowsMultipleRecipients && !!valueOptions.length) {
      return valueOptions.concat([onlyOneRecipientDummyOption]);
    }
    const hasOptionsMatchingSearch = options && options.some(option => optionsContainsString(searchText.current, option));
    if (hasOptionsMatchingSearch) {
      return valueOptions.concat(...options);
    } else if (searchText.current.trim()) {
      searchResults.current[searchText.current] = [createNewEmailEntry()];
      return valueOptions.concat([{
        address: searchText.current,
        clearableValue: true,
        name: '',
        text: I18n.text('composer-ui.metadata-editor.email-recipient-search-input.create-new-option', {
          recipientEmail: searchText.current
        }),
        value: searchText.current
      }]);
    } else {
      // dummyOption for bypassing default 'all options are selected` text
      const dummyOption = {
        name: '',
        address: '',
        text: I18n.text('composer-ui.metadata-editor.email-recipient-search-input.type-to-search'),
        value: '',
        clearableValue: false,
        disabled: true
      };
      return valueOptions.concat([dummyOption]);
    }
  };
  const [selectRef, setSelectRef] = useState(null);
  useImperativeHandle(ref, () => {
    // @ts-expect-error we are assuming that the input ref is a RefObject
    const currentDefault = ref && ref.current && ref.current.defaultField;
    return {
      index,
      currentField: selectRef,
      defaultField: index === 0 ? selectRef : currentDefault || null,
      hasValue: internalValue && internalValue.length > 0
    };
  }, [ref, index, internalValue, selectRef]);
  const recipientSelect = /*#__PURE__*/_jsx(View, {
    typography: "body-100",
    flex: 1,
    children: /*#__PURE__*/_jsx(Select, Object.assign({}, rest, {
      autoFocus: autoFocus,
      valueComponent: GenericRecipientTag,
      autoload: false,
      cache: {},
      clearable: false,
      minimumSearchCount: 2,
      noResultsText: valueOptions.length && !channelAllowsMultipleRecipients ? I18n.text('composer-ui.metadata-editor.generic-recipient-search-input.onlyOneRecipient') : undefined,
      multi: true,
      onChange: handleChange,
      onInputChange: handleLoadOptions,
      options: getOptions(),
      placeholder: I18n.text('composer-ui.metadata-editor.email-recipient-search-input.placeholder'),
      searchPromptText: '',
      value: internalValue,
      ref: setSelectRef
    }))
  });
  return contactProperty === HS_PHONE_NUMBER ? /*#__PURE__*/_jsx(WARecipientShepherd, {
    children: recipientSelect
  }) : recipientSelect;
};
_GenericRecipientSearchInput.displayName = 'GenericRecipientSearchInput';
const GenericRecipientSearchInput = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(_GenericRecipientSearchInput));
export default GenericRecipientSearchInput;